import { formatCurrency, MONTH_LABELS, MONTH_SHORT_LABELS } from '@crm/shared';
import { Card, CardContent, cn, Modal, Select } from '@crm/ui';
import {
  CalendarDays,
  CalendarRange,
  CalendarX,
  ChartColumn,
  ChartLine,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  MessageCircleWarning,
  Pencil,
  Receipt,
  Sparkles,
  Target,
  UserX,
} from 'lucide-react';
import { useState } from 'react';
import { BarChart, type ChartVariant, MarcaDeInfo, StatCard, WIDGET_GRID } from '@/components';
import { GoalsModal } from '@/modules/company/components/goals-form';
import { LEADS_ROUTES } from '@/modules/leads/api';
import type { DashboardData, Kpis, ServedPoint } from '../api';
import type { DashboardPeriod } from './dashboard-filters';

/** Barra de progresso da meta: verde quando chega la, ambar no caminho. */
function GoalCard({
  title,
  current,
  goal,
  progress,
  format,
  about,
}: {
  title: string;
  current: string;
  goal: number | null;
  progress: number | null;
  format: (value: number) => string;
  /** O que a conta significa - vira o simbolo de informacao ao lado do numero. */
  about?: { title: string; body: string[] };
}) {
  const [explicando, setExplicando] = useState(false);
  const [editando, setEditando] = useState(false);
  const percent = Math.min(100, progress ?? 0);

  return (
    <>
      {/* Mesma pele do StatCard, sem o hover: o cartao nao leva a lugar nenhum.
        O unico caminho para as metas e o lapis ao lado do numero - o cartao
        inteiro clicavel mandava para outra tela quem so queria ler o
        progresso. */}
      <Card className="relative h-full overflow-hidden rounded-sm border-border-strong bg-card shadow-[0_1px_2px_0_rgba(15,23,42,0.05),0_2px_6px_-2px_rgba(15,23,42,0.10)] dark:bg-white/[0.06] dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.5)]">
        <span
          aria-hidden
          className="pointer-events-none absolute right-2 top-2 text-foreground/[0.07] dark:text-white/[0.10]"
        >
          <Target className="h-10 w-10" />
        </span>

        <CardContent className="relative space-y-2 p-3 pt-8 sm:p-4 sm:pt-9">
          <span className="block min-w-0">
            <span className="block text-base font-medium text-slate-600 dark:text-slate-300">
              {title}
            </span>
            <span className="mt-0.5 flex items-center gap-3 text-lg font-semibold tracking-tight sm:text-xl">
              {current}

              {about ? (
                <button
                  type="button"
                  aria-label={`O que e ${about.title}`}
                  onClick={() => setExplicando(true)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <MarcaDeInfo />
                </button>
              ) : null}
            </span>
          </span>

          {goal ? (
            <>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full',
                    percent >= 100 ? 'bg-emerald-500' : 'bg-primary',
                  )}
                  style={{ width: `${percent}%` }}
                />
              </div>
              {/* Progresso a esquerda, meta a direita: as duas pontas da barra
                que esta logo acima. */}
              <p className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                <span>{Math.round(progress ?? 0)}%</span>

                {/* A meta e o unico numero da tela que se muda daqui - o lapis
                  ao lado dela abre a edicao em modal, sem tirar ninguem do
                  painel. Sem a palavra "Meta": a barra logo acima ja diz o
                  que e, e o rotulo roubava espaco do numero. */}
                <button
                  type="button"
                  title="Editar metas do mes"
                  onClick={() => setEditando(true)}
                  className="flex items-center gap-1 font-medium text-foreground transition-colors hover:text-primary"
                >
                  {format(goal)}
                  <Pencil className="h-3 w-3" />
                </button>
              </p>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setEditando(true)}
              className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-primary"
            >
              Definir meta
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </CardContent>
      </Card>

      {editando ? <GoalsModal onClose={() => setEditando(false)} /> : null}

      {about ? (
        <Modal
          open={explicando}
          onClose={() => setExplicando(false)}
          title={about.title}
          size="small"
          actions={[{ label: 'Entendi', onClick: () => setExplicando(false) }]}
        >
          <div className="space-y-3 text-sm text-muted-foreground">
            {about.body.map((paragrafo) => (
              <p key={paragrafo}>{paragrafo}</p>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/**
 * Cabecalho de bloco do painel. Sai do cinza claro: eram tantos rotulos
 * apagados que a tela nao tinha onde comecar a ler.
 */
function Secao({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2.5">
      {title ? (
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
      ) : null}
      {children}
    </section>
  );
}

/** Alterna colunas e linha - dois botoes, sem abrir menu para uma escolha. */
function VariantToggle({
  value,
  onChange,
  label,
}: {
  value: ChartVariant;
  onChange: (value: ChartVariant) => void;
  label: string;
}) {
  const opcoes = [
    { key: 'bars' as const, icon: ChartColumn, texto: 'Colunas' },
    { key: 'line' as const, icon: ChartLine, texto: 'Linha' },
  ];

  return (
    // Sem `role="group"`: com dois graficos na mesma tela, o que identifica o
    // botao e o proprio rotulo dele ("Clientes por mes: linha").
    <div className="flex shrink-0 items-center gap-0.5">
      {opcoes.map((opcao) => (
        <button
          key={opcao.key}
          type="button"
          aria-label={`${label}: ${opcao.texto.toLowerCase()}`}
          aria-pressed={value === opcao.key}
          onClick={() => onChange(opcao.key)}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
            value === opcao.key
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <opcao.icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}

/** Titulo do cartao de grafico com os controles na mesma linha. */
/**
 * O titulo do grafico e os controles dele. Quando o proprio titulo e uma
 * escolha - "Atendimentos realizados" ou "Vendas" -, passa-se um no no lugar
 * do texto: repetir o nome no titulo e no select dizia a mesma coisa duas
 * vezes na mesma linha.
 */
/** Vira a pagina do grafico. Grande de proposito: e alvo de dedo, ao lado dele. */
function SetaGrafico({
  direcao,
  label,
  disabled,
  onClick,
}: {
  direcao: 'anterior' | 'proxima';
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const Icone = direcao === 'anterior' ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className="flex h-16 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-25"
    >
      <Icone className="h-7 w-7" />
    </button>
  );
}

function ChartHeader({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      {typeof title === 'string' ? (
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      ) : (
        title
      )}
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

/** Quem o grafico de clientes conta. */
type RecorteCliente = 'all' | 'new' | 'returning';

const RECORTE_LABELS: Record<RecorteCliente, string> = {
  all: 'Todos',
  new: 'Novos',
  returning: 'Retornos',
};

/** Como o grafico de atendimentos recorta o tempo. */
type ServedView = 'hour' | 'day' | 'month' | 'quarter';

const SERVED_LABELS: Record<ServedView, string> = {
  hour: 'Por hora',
  day: 'Por dia',
  month: 'Por mes',
  quarter: 'Por trimestre',
};

/** Dias que a janela deslizante mostra de uma vez. */
const JANELA = 15;

/**
 * Os quinze dias que terminam em `fim`, do mais antigo ao mais novo, com o
 * numero de cada um vindo da serie do ano. Dia que a serie nao tem aparece
 * zerado: e assim que se pode voltar antes do inicio dos dados sem a tela
 * quebrar.
 */
function janelaDeDias(pontos: ServedPoint[], fim: Date) {
  const porData = new Map(pontos.map((ponto) => [ponto.label, ponto.count]));

  return Array.from({ length: JANELA }, (_, index) => {
    const dia = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate() - (JANELA - 1 - index));
    const label = `${String(dia.getDate()).padStart(2, '0')}/${String(dia.getMonth() + 1).padStart(2, '0')}`;

    return { label, count: porData.get(label) ?? 0 };
  });
}

/** Os doze meses somados de tres em tres. */
function porTrimestre(meses: ServedPoint[]) {
  return [1, 2, 3, 4].map((trimestre) => ({
    label: `${trimestre}o tri`,
    count: meses
      .slice((trimestre - 1) * 3, trimestre * 3)
      .reduce((soma, mes) => soma + mes.count, 0),
  }));
}

/**
 * Recorta as horas mortas das pontas: 24 colunas para um salao que abre as 9
 * e fecha as 19 sao dez colunas de nada em volta do movimento.
 */
function janelaDoDia(pontos: ServedPoint[]) {
  const primeiro = pontos.findIndex((ponto) => ponto.count > 0);
  if (primeiro < 0) return pontos.slice(8, 21);

  let ultimo = primeiro;
  pontos.forEach((ponto, index) => {
    if (ponto.count > 0) ultimo = index;
  });

  return pontos.slice(Math.max(0, primeiro - 1), Math.min(pontos.length, ultimo + 2));
}

const SERIE_CLIENTES = [
  { key: 'customers', label: 'Clientes', color: 'bg-blue-500', stroke: 'text-blue-500' },
];
const SERIE_ATENDIMENTOS = [
  { key: 'count', label: 'Atendimentos', color: 'bg-emerald-500', stroke: 'text-emerald-500' },
];

const SERIE_VENDAS = [
  { key: 'count', label: 'Vendas', color: 'bg-sky-500', stroke: 'text-sky-500' },
];

/** O que o grafico mede: quantos atendimentos, ou quanto eles renderam. */
type ServedMetric = 'served' | 'revenue';

/** Os widgets do painel: agenda, clientes, cashback, graficos e metas. */
export function DashboardWidgets({
  data,
  kpis,
  period,
  onYearChange,
  beforeCharts,
}: {
  data?: DashboardData;
  kpis?: Kpis;
  /** Bloco que entra logo antes dos graficos - hoje, o funil de conversao. */
  beforeCharts?: React.ReactNode;
  /** A competencia olhada - e ela que nomeia os recortes do mes. */
  period: DashboardPeriod;
  /** Vira o ano do painel pelas setas do grafico de clientes. */
  onYearChange?: (year: number) => void;
}) {
  /** So o celular usa: 1 = janeiro a junho, 2 = julho a dezembro. */
  const [semester, setSemester] = useState<1 | 2>(() => (new Date().getMonth() < 6 ? 1 : 2));
  const [clientesVariant, setClientesVariant] = useState<ChartVariant>('bars');
  const [recorte, setRecorte] = useState<RecorteCliente>('all');
  const [servedVariant, setServedVariant] = useState<ChartVariant>('line');
  const [servedView, setServedView] = useState<ServedView>('day');
  const [servedMetric, setServedMetric] = useState<ServedMetric>('served');
  /**
   * Quantas janelas de quinze dias para tras. Zero e a que termina hoje - so
   * o futuro nao existe, e o passado nao tem limite.
   */
  const [janelasAtras, setJanelasAtras] = useState(0);

  const clientesData = (data?.byMonth ?? []).map((month) => ({
    label: `${(MONTH_SHORT_LABELS[month.month - 1] ?? String(month.month)).toUpperCase()} ${String(
      period.year,
    ).slice(2)}`,
    // Sigla e ano: navegando entre anos, "JAN" sozinho nao diz de qual. O ano
    // vem mais claro porque e desempate, nao informacao principal - mas no
    // mesmo corpo, para a etiqueta nao ficar com dois pesos.
    labelNode: (
      <>
        {(MONTH_SHORT_LABELS[month.month - 1] ?? String(month.month)).toUpperCase()}{' '}
        <span className="opacity-60">{String(period.year).slice(2)}</span>
      </>
    ),
    values: {
      customers:
        recorte === 'new'
          ? month.newCustomers
          : recorte === 'returning'
            ? month.returningCustomers
            : month.customers,
    },
  }));
  const semestreData = semester === 1 ? clientesData.slice(0, 6) : clientesData.slice(6);

  // Vendas so existe para quem pode ver dinheiro: a API devolve nulo, e a
  // opcao nem aparece na lista. Se a permissao cair com o grafico aberto, a
  // serie volta sozinha para atendimentos.
  const podeVerVendas = Boolean(data?.revenue);
  const medida = podeVerVendas && servedMetric === 'revenue' ? 'revenue' : 'served';
  const serie = medida === 'revenue' ? data?.revenue : data?.served;

  // O fim da janela: hoje, menos quinze dias para cada passo atras.
  const hoje = new Date();
  const fimDaJanela = new Date(
    hoje.getFullYear(),
    hoje.getMonth(),
    hoje.getDate() - janelasAtras * JANELA,
  );

  const servedFonte =
    servedView === 'hour'
      ? janelaDoDia(serie?.byHour ?? [])
      : servedView === 'day'
        ? // Trinta e um dias numa coluna do painel viram tracos: quinze e o
          // recorte que ainda da para ler.
          janelaDeDias(serie?.byDay ?? [], fimDaJanela)
        : servedView === 'quarter'
          ? porTrimestre(serie?.byMonth ?? [])
          : (serie?.byMonth ?? []);

  const servedData = servedFonte.map((ponto) => ({
    label:
      servedView === 'month'
        ? (MONTH_SHORT_LABELS[Number(ponto.label) - 1] ?? ponto.label)
        : ponto.label,
    values: { count: ponto.count },
  }));

  const mesAtual = MONTH_LABELS[period.month - 1] ?? '';
  // Mes sem horario nenhum nao tem taxa de falta - zero sobre zero e zero.
  const taxaDeFalta = kpis?.total ? Math.round(((kpis.noShow ?? 0) / kpis.total) * 1000) / 10 : 0;
  const goals = data?.goals;
  // Sem lead no mes nao ha taxa: o cartao mostra o traco, nao zero por cento.
  const winRate = goals?.winRate;

  return (
    <div className="space-y-8">
      <Secao>
        <div className={WIDGET_GRID.four}>
          <GoalCard
            title={`Novos clientes ${mesAtual}`}
            current={String(data?.customers.newMonth ?? 0)}
            goal={goals?.customers ?? null}
            progress={goals?.customersProgress ?? null}
            format={(value) => String(value)}
          />
          <GoalCard
            title={`Retornos em ${mesAtual}`}
            current={String(goals?.returningMonth ?? 0)}
            goal={goals?.returning ?? null}
            progress={goals?.returningProgress ?? null}
            about={{
              title: 'Retornos',
              body: [
                'Clientes de casa que voltaram no mes - quem ja era cadastrado antes dele e teve atendimento agora.',
                'Cliente novo nao entra aqui: ele conta em "Novos clientes". Sao duas contas diferentes, e um mes pode ir bem numa e mal na outra.',
              ],
            }}
            format={(value) => String(value)}
          />
          <GoalCard
            title={`Win rate em ${mesAtual}`}
            current={winRate === null || winRate === undefined ? '--' : `${winRate}%`}
            goal={goals?.winRateGoal ?? null}
            progress={goals?.winRateProgress ?? null}
            format={(value) => `${value}%`}
            about={{
              title: 'Win rate',
              body: [
                'Quanto dos leads que entraram no mes chegou a marcar horario - agendados divididos por novos leads.',
                'Mede o atendimento, nao a divulgacao: trazer mais gente derruba a taxa se ninguem responder mais rapido.',
                'Mes sem lead nenhum aparece como traco, nao como zero: nao ha o que dividir.',
              ],
            }}
          />
          <StatCard
            title="Ticket medio"
            value={formatCurrency(kpis?.averageTicket ?? 0)}
            icon={<Receipt />}
            info
            about={{
              title: 'Ticket medio',
              body: [
                'Vendas divididas pelos atendimentos concluidos.',
                'Sobe com combo e servico adicional, nao com aumento de preco - e o caminho mais curto para faturar mais sem lotar a agenda.',
              ],
            }}
          />
        </div>
      </Secao>

      {/* Um risco fino separa o que se persegue do que ja aconteceu - sem ele
          os dois blocos de numeros pareciam um so. */}
      <hr className="border-border" />

      {/* Tres grupos de quatro, uma linha para cada um: primeiro a agenda
          de hoje e da semana, depois o resultado do mes e por fim os
          clientes. Em seis colunas os grupos partiam no meio da linha. */}
      <Secao>
        <div className={WIDGET_GRID.four}>
          <StatCard
            title="Agendados hoje"
            value={data?.agenda.appointmentsToday ?? 0}
            icon={<CalendarDays />}
            about={{
              title: 'Agendados hoje',
              body: [
                'Horarios marcados para hoje, sem contar desistencias e cancelamentos.',
                'E a carga do dia: compare com os atendidos para saber quanto da agenda virou atendimento de fato.',
              ],
            }}
          />
          <StatCard
            title="Agendados na semana"
            value={data?.agenda.appointmentsWeek ?? 0}
            icon={<CalendarRange />}
            about={{
              title: 'Agendados na semana',
              body: [
                'Horarios marcados de domingo a sabado da semana corrente.',
                'Semana curta com dias livres a frente e o momento de acionar a lista de quem nao retorna.',
              ],
            }}
          />
          <StatCard
            title="Atendidos hoje"
            value={data?.agenda.servedToday ?? 0}
            icon={<ClipboardCheck />}
            about={{
              title: 'Atendidos hoje',
              body: [
                'Atendimentos ja encerrados hoje - o horario so entra aqui depois do check-out.',
                'Fica abaixo dos agendados enquanto o dia corre; a distancia no fim do expediente e o que nao aconteceu.',
              ],
            }}
          />
          <StatCard
            title="Atendidos na semana"
            value={data?.agenda.servedWeek ?? 0}
            icon={<ClipboardList />}
            about={{
              title: 'Atendidos na semana',
              body: [
                'Atendimentos encerrados na semana corrente.',
                'E a base do faturamento do periodo: cada um deles gerou pagamento, comissao e cashback.',
              ],
            }}
          />

          <StatCard
            title="Novos leads"
            value={data?.leads.newMonth ?? 0}
            icon={<Sparkles />}
            about={{
              title: 'Novos leads',
              body: [
                'Contatos que entraram no funil na competencia olhada, venham de campanha, do balcao ou de quem escreveu no WhatsApp.',
                'E a boca do funil: sem entrada nova, o agendamento do mes que vem sai do que ja esta na base.',
              ],
            }}
          />
          <StatCard
            title="Follow-ups pendentes"
            shortTitle="Follow-ups"
            value={data?.leads.pendingFollowUps ?? 0}
            icon={<MessageCircleWarning />}
            info
            to={LEADS_ROUTES.BOARD}
            about={{
              title: 'Follow-ups pendentes',
              body: [
                'Leads abertos em que a ultima palavra foi do cliente e ninguem do salao respondeu - mensagem automatica nao encerra a pendencia.',
                'E a fila que custa mais caro: sao pessoas que ja demonstraram interesse e estao esperando.',
              ],
            }}
          />
          <StatCard
            title="Leads nao convertidos"
            shortTitle="Nao convertidos"
            value={data?.leads.lost ?? 0}
            icon={<UserX />}
            info
            about={{
              title: 'Leads nao convertidos',
              body: [
                'Leads que nao agendaram: entraram no funil, nunca marcaram horario e estao parados ha mais de 30 dias.',
                'Lead que se mexeu na semana passada nao entra aqui, por mais antigo que seja - o que conta e ter parado, nao ter envelhecido.',
                'E a lista mais barata de reativar: sao pessoas que ja procuraram o salao uma vez.',
              ],
            }}
          />
          <StatCard
            title="Taxa de no-show"
            shortTitle="No-show"
            value={`${taxaDeFalta}%`}
            icon={<CalendarX />}
            info
            about={{
              title: 'Taxa de no-show',
              body: [
                'Quanto dos horarios do mes o cliente simplesmente nao apareceu, sem avisar - cancelamento com aviso nao entra aqui.',
                'E o furo mais caro da agenda: o horario ficou reservado, o profissional ficou parado e ninguem pagou por ele.',
                'Acima de 10%, o caminho costuma ser confirmacao no dia anterior, nao cobranca de multa.',
              ],
            }}
          />
        </div>
      </Secao>

      {/* Os dois graficos dividem a linha no desktop: um conta quem entra, o
          outro conta o que o salao entrega. */}
      {beforeCharts}

      <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <Card variant="flush">
          <CardContent className="space-y-3">
            <ChartHeader
              title={
                podeVerVendas ? (
                  <Select
                    aria-label="Medida do grafico"
                    className="h-auto w-auto border-0 bg-transparent py-0 pl-0 pr-7 text-base font-semibold tracking-tight shadow-none focus-visible:ring-0"
                    value={medida}
                    onChange={(event) => setServedMetric(event.target.value as ServedMetric)}
                  >
                    <option value="served">Atendimentos realizados</option>
                    <option value="revenue">Vendas</option>
                  </Select>
                ) : (
                  'Atendimentos realizados'
                )
              }
            >
              <Select
                aria-label="Periodo do grafico de atendimentos"
                className="h-auto w-auto border-0 bg-transparent py-0 pl-0 pr-6 text-right text-sm font-medium text-primary shadow-none focus-visible:ring-0"
                value={servedView}
                onChange={(event) => setServedView(event.target.value as ServedView)}
              >
                {(Object.keys(SERVED_LABELS) as ServedView[]).map((option) => (
                  <option key={option} value={option}>
                    {SERVED_LABELS[option]}
                  </option>
                ))}
              </Select>
              <VariantToggle
                value={servedVariant}
                onChange={setServedVariant}
                label={medida === 'revenue' ? 'Vendas' : 'Atendimentos realizados'}
              />
            </ChartHeader>

            {/* A quinzena se troca virando a pagina, com as setas ladeando o
                desenho: e o mesmo gesto do calendario, e o grafico nao perde
                largura para um controle no rodape. */}
            <div className="flex items-center gap-1">
              {servedView === 'day' ? (
                <SetaGrafico
                  direcao="anterior"
                  label="Quinze dias antes"
                  disabled={false}
                  onClick={() => setJanelasAtras((atual) => atual + 1)}
                />
              ) : null}

              <div className="min-w-0 flex-1">
                <BarChart
                  data={servedData}
                  series={medida === 'revenue' ? SERIE_VENDAS : SERIE_ATENDIMENTOS}
                  variant={servedVariant}
                  height={170}
                  // A janela se navega: some com o grafico e volta, a cada
                  // passo, cansa. Quinze dias sem movimento sao quinze zeros.
                  drawWhenEmpty
                  tiltLabels={servedView === 'day'}
                />
              </div>

              {servedView === 'day' ? (
                <SetaGrafico
                  direcao="proxima"
                  label="Quinze dias depois"
                  disabled={janelasAtras === 0}
                  onClick={() => setJanelasAtras((atual) => Math.max(0, atual - 1))}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card variant="flush">
          <CardContent className="space-y-3">
            <ChartHeader title="Clientes por mes">
              <Select
                aria-label="Recorte do grafico de clientes"
                className="h-auto w-auto border-0 bg-transparent py-0 pl-0 pr-6 text-right text-sm font-medium text-primary shadow-none focus-visible:ring-0"
                value={recorte}
                onChange={(event) => setRecorte(event.target.value as RecorteCliente)}
              >
                {(Object.keys(RECORTE_LABELS) as RecorteCliente[]).map((option) => (
                  <option key={option} value={option}>
                    {RECORTE_LABELS[option]}
                  </option>
                ))}
              </Select>
              <VariantToggle
                value={clientesVariant}
                onChange={setClientesVariant}
                label="Clientes por mes"
              />
            </ChartHeader>

            {/* Meio ano por vez no celular, como na previsao do financeiro:
                doze barras em 390px viram tracos. O desktop segue com o ano.
                As setas viram o ano nos dois. */}
            <div className="flex items-center gap-1">
              {onYearChange ? (
                <SetaGrafico
                  direcao="anterior"
                  label="Ano anterior"
                  disabled={false}
                  onClick={() => onYearChange(period.year - 1)}
                />
              ) : null}

              <div className="min-w-0 flex-1">
                <BarChart
                  className="sm:hidden"
                  data={semestreData}
                  tiltLabels
                  series={SERIE_CLIENTES}
                  variant={clientesVariant}
                  height={170}
                  emptyLabel="Nenhum cliente no periodo."
                  aside={
                    <div className="flex shrink-0 gap-3">
                      {([1, 2] as const).map((metade) => (
                        <button
                          key={metade}
                          type="button"
                          onClick={() => setSemester(metade)}
                          className={cn(
                            'text-xs transition-colors',
                            semester === metade
                              ? 'font-medium text-primary underline underline-offset-4'
                              : 'text-muted-foreground',
                          )}
                        >
                          {metade === 1 ? '1o semestre' : '2o semestre'}
                        </button>
                      ))}
                    </div>
                  }
                />
                <BarChart
                  className="hidden sm:block"
                  data={clientesData}
                  tiltLabels
                  series={SERIE_CLIENTES}
                  variant={clientesVariant}
                  height={170}
                  emptyLabel="Nenhum cliente no periodo."
                />
              </div>

              {onYearChange ? (
                <SetaGrafico
                  direcao="proxima"
                  label="Proximo ano"
                  disabled={period.year >= new Date().getFullYear()}
                  onClick={() => onYearChange(period.year + 1)}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
